import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { UserX, User, Lock, Copy, CheckCircle2, ArrowRight, ArrowLeft, ShieldCheck, AlertTriangle } from "lucide-react";
import EvidenceUpload from "@/components/EvidenceUpload";

const STEPS = ["Sigilo", "Categoria", "Detalhes", "Confirmação", "Protocolo"];

export default function Denunciar() {
  const { slug: slugParam } = useParams();
  const slug = slugParam || "acme";
  const nav = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    mode: "anonymous",
    category: "",
    occurred_at: "",
    occurrence_period: "",
    location: "",
    description: "",
    involved: "",
    ghe_sector: "",
    reporter_name: "",
    reporter_contact: "",
    followup_password: "",
    followup_password_confirm: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    api.get(`/public/tenants/${slug}`)
      .then((r) => setTenant(r.data))
      .catch(() => toast.error("Empresa não encontrada"));
  }, [slug]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const next = () => {
    if (step === 1 && !form.category) return toast.error("Escolha uma categoria");
    if (step === 2 && form.description.trim().length < 10) return toast.error("Descreva o ocorrido com pelo menos 10 caracteres");
    if (step === 3) {
      if (form.followup_password.length < 6) return toast.error("Senha de acompanhamento precisa de 6+ caracteres");
      if (form.followup_password !== form.followup_password_confirm) return toast.error("As senhas não conferem");
      if (form.mode === "identified" && (!form.reporter_name || !form.reporter_contact)) return toast.error("Informe nome e contato ou selecione o modo anônimo");
      return submit();
    }
    setStep(step + 1);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, attachment_ids: files.map((f) => f.id) };
      delete payload.followup_password_confirm;
      const r = await api.post(`/public/${slug}/complaints`, payload);
      setResult(r.data);
      setStep(4);
      toast.success("Denúncia registrada com sigilo");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!tenant) return <div className="min-h-[60vh] grid place-items-center text-slate-400">Carregando canal…</div>;

  if (tenant.active === false) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="channel-inactive">
        <AlertTriangle className="w-14 h-14 mx-auto text-amber-400" />
        <h1 className="font-display text-3xl font-bold text-white mt-6">Canal temporariamente indisponível</h1>
        <p className="text-slate-400 mt-3">O canal de denúncia de <span className="text-white">{tenant.name}</span> está suspenso até a regularização da assinatura. Se você é da equipe de compliance, acesse o painel para reativar.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-500/15 border border-teal-500/30 text-teal-300 text-xs font-semibold uppercase tracking-widest mb-4">
          <ShieldCheck className="w-3.5 h-3.5" /> Canal Seguro — {tenant.name}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">Registre sua denúncia</h1>
        <p className="mt-3 text-slate-400 text-sm max-w-xl mx-auto">
          Todos os dados são criptografados. No modo anônimo, nenhum metadado identificável é armazenado.
        </p>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-between mb-8 text-xs">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1 flex items-center">
            <div className={`w-8 h-8 rounded-full grid place-items-center font-semibold ${i <= step ? "bg-indigo-500 text-white" : "bg-slate-800 text-slate-500"}`}>
              {i + 1}
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < step ? "bg-indigo-500" : "bg-slate-800"}`} />}
          </div>
        ))}
      </div>

      <Card className="p-6 sm:p-8 bg-slate-900/60 border-slate-800">
        {step === 0 && (
          <div className="space-y-6 fade-up">
            <div>
              <h2 className="font-display text-xl font-semibold text-white mb-2">Como deseja registrar?</h2>
              <p className="text-sm text-slate-400">Sua escolha define o nível de sigilo. Ambos são igualmente investigados.</p>
            </div>
            <RadioGroup value={form.mode} onValueChange={(v) => upd("mode", v)} className="grid sm:grid-cols-2 gap-4">
              <label htmlFor="anon" className={`cursor-pointer p-5 rounded-xl border-2 transition ${form.mode === "anonymous" ? "border-teal-500 bg-teal-500/10" : "border-slate-700 bg-slate-800/40"}`}>
                <RadioGroupItem id="anon" value="anonymous" className="sr-only" data-testid="mode-anonymous" />
                <UserX className="w-6 h-6 text-teal-300 mb-3" />
                <div className="font-semibold text-white">Anônima</div>
                <div className="text-xs text-slate-400 mt-1">Sem coleta de IP, e-mail ou cookies. Total anonimato.</div>
              </label>
              <label htmlFor="id" className={`cursor-pointer p-5 rounded-xl border-2 transition ${form.mode === "identified" ? "border-indigo-500 bg-indigo-500/10" : "border-slate-700 bg-slate-800/40"}`}>
                <RadioGroupItem id="id" value="identified" className="sr-only" data-testid="mode-identified" />
                <User className="w-6 h-6 text-indigo-300 mb-3" />
                <div className="font-semibold text-white">Identificada</div>
                <div className="text-xs text-slate-400 mt-1">Sua identidade é sigilosa e visível apenas para o compliance autorizado.</div>
              </label>
            </RadioGroup>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6 fade-up">
            <div>
              <h2 className="font-display text-xl font-semibold text-white mb-2">Categoria da ocorrência</h2>
              <p className="text-sm text-slate-400">Selecione a categoria que melhor descreve o fato.</p>
            </div>
            <div className="grid gap-3">
              {tenant.categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  data-testid={`cat-${c}`}
                  onClick={() => upd("category", c)}
                  className={`text-left p-4 rounded-lg border transition ${form.category === c ? "border-teal-500 bg-teal-500/10" : "border-slate-700 bg-slate-800/40 hover:border-slate-600"}`}
                >
                  <div className="font-medium text-white">{c}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5 fade-up">
            <div>
              <h2 className="font-display text-xl font-semibold text-white mb-2">Detalhes do ocorrido</h2>
              <p className="text-sm text-slate-400">Quanto mais preciso, mais eficaz a investigação.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Data (opcional)</Label>
                <Input type="date" value={form.occurred_at} onChange={(e) => upd("occurred_at", e.target.value)} data-testid="input-occurred-at" className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div>
                <Label className="text-slate-300">Ou período aproximado</Label>
                <Input placeholder="Ex.: últimos 3 meses" value={form.occurrence_period} onChange={(e) => upd("occurrence_period", e.target.value)} data-testid="input-period" className="bg-slate-800 border-slate-700 text-white" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Local / unidade</Label>
                <Input placeholder="Ex.: Filial SP, obra Y, remoto" value={form.location} onChange={(e) => upd("location", e.target.value)} data-testid="input-location" className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div>
                <Label className="text-slate-300">Setor / GHE (NR-1)</Label>
                <Input placeholder="Ex.: Operação Produção" value={form.ghe_sector} onChange={(e) => upd("ghe_sector", e.target.value)} data-testid="input-ghe" className="bg-slate-800 border-slate-700 text-white" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Descrição dos fatos *</Label>
              <Textarea rows={6} placeholder="Descreva o que aconteceu, como, quando e quem estava envolvido…" value={form.description} onChange={(e) => upd("description", e.target.value)} data-testid="input-description" className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Pessoas envolvidas (opcional)</Label>
              <Input placeholder="Ex.: gerente do setor X" value={form.involved} onChange={(e) => upd("involved", e.target.value)} data-testid="input-involved" className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Evidências (opcional)</Label>
              <div className="mt-2"><EvidenceUpload slug={slug} files={files} onChange={setFiles} /></div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5 fade-up">
            <div>
              <h2 className="font-display text-xl font-semibold text-white mb-2">Acompanhamento seguro</h2>
              <p className="text-sm text-slate-400">Defina uma senha para acompanhar o caso e trocar mensagens com a equipe. Guarde-a em local seguro.</p>
            </div>
            {form.mode === "identified" && (
              <div className="grid sm:grid-cols-2 gap-4 p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/30">
                <div>
                  <Label className="text-slate-300">Seu nome</Label>
                  <Input value={form.reporter_name} onChange={(e) => upd("reporter_name", e.target.value)} data-testid="input-reporter-name" className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">Contato (e-mail ou telefone)</Label>
                  <Input value={form.reporter_contact} onChange={(e) => upd("reporter_contact", e.target.value)} data-testid="input-reporter-contact" className="bg-slate-800 border-slate-700 text-white" />
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Senha de acompanhamento *</Label>
                <Input type="password" value={form.followup_password} onChange={(e) => upd("followup_password", e.target.value)} data-testid="input-followup-password" className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div>
                <Label className="text-slate-300">Confirme a senha *</Label>
                <Input type="password" value={form.followup_password_confirm} onChange={(e) => upd("followup_password_confirm", e.target.value)} data-testid="input-followup-password-confirm" className="bg-slate-800 border-slate-700 text-white" />
              </div>
            </div>
            <div className="text-xs text-slate-500 flex items-start gap-2">
              <Lock className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />
              A senha é armazenada em hash bcrypt. Nem a equipe de compliance nem o vixvox conseguem recuperá-la. Anote-a.
            </div>
          </div>
        )}

        {step === 4 && result && (
          <div className="space-y-6 text-center fade-up">
            <div className="w-16 h-16 rounded-full bg-teal-500/20 border border-teal-500/40 grid place-items-center mx-auto">
              <CheckCircle2 className="w-9 h-9 text-teal-300" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-bold text-white">Denúncia registrada com sucesso</h2>
              <p className="text-sm text-slate-400 mt-2">Guarde seu protocolo e a senha definida. Você os usará para acompanhar o caso.</p>
            </div>
            <div className="p-6 rounded-xl bg-slate-800/60 border border-slate-700 text-left">
              <div className="text-xs uppercase tracking-widest text-teal-400 mb-2">Protocolo único</div>
              <div className="flex items-center justify-between gap-3">
                <div className="font-mono text-2xl text-white" data-testid="result-protocol">{result.protocol}</div>
                <Button variant="outline" onClick={() => { navigator.clipboard.writeText(result.protocol); toast.success("Protocolo copiado"); }} data-testid="btn-copy-protocol" className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-700">
                  <Copy className="w-4 h-4 mr-2" /> Copiar
                </Button>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => nav("/acompanhar")} data-testid="btn-goto-followup" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                Ir para o acompanhamento <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" onClick={() => nav("/")} data-testid="btn-back-home" className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800">
                Voltar ao início
              </Button>
            </div>
          </div>
        )}

        {step < 4 && (
          <div className="flex justify-between mt-8 pt-6 border-t border-slate-800">
            <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="btn-step-back" className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800">
              <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
            </Button>
            <Button onClick={next} disabled={submitting} data-testid="btn-step-next" className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {step === 3 ? (submitting ? "Enviando…" : "Enviar denúncia") : "Continuar"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </Card>

      <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
        <Badge variant="outline" className="border-slate-700 bg-slate-900 text-teal-300">TLS 1.2+</Badge>
        <Badge variant="outline" className="border-slate-700 bg-slate-900 text-teal-300">AES-256</Badge>
        <Badge variant="outline" className="border-slate-700 bg-slate-900 text-teal-300">ISO 27001</Badge>
      </div>
    </div>
  );
}
